import { useState, useCallback, useRef, useEffect } from 'react'
import { useChatStore } from '@/stores/chat-store'
import { usePanelSelectionStore } from '@/stores/panel-selection-store'
import { resolvePanelTitle, resolvePanelComponentType } from '@/workspace/panel-title'

export interface UseChatTextareaOptions {
  onSend: (text: string) => void
  disabled?: boolean
  /** Text to pre-fill the input with once (e.g. "AAPL on 2024-03-01: " from a
   *  chart's "Ask AI about this day"). Consumed on change: seeds the value,
   *  focuses, moves the cursor to the end, then calls `onSeedConsumed`. */
  seedText?: string | null
  /** Called right after `seedText` is applied, so the owner can clear it. */
  onSeedConsumed?: () => void
  /** Mirrors every value change out (quick-action filtering in the floating
   *  window). Purely observational — the input stays uncontrolled. */
  onValueChange?: (value: string) => void
  /** Live text layered onto the display (dictation interim). Not part of the
   *  committed value — the final transcript arrives via `appendText`. */
  overlayText?: string
}

/**
 * The composer's textarea brain, shared by every surface: uncontrolled value
 * state, external seed adoption, Enter-to-send, auto-resize, and the
 * `@`-mention panel-select flow. Extracted from the original ChatInput so the
 * battle-tested logic lives in one testable unit while CompanionComposer owns
 * only layout.
 */
export function useChatTextarea({
  onSend,
  disabled,
  seedText,
  onSeedConsumed,
  onValueChange,
  overlayText,
}: UseChatTextareaOptions) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Index of the "@" that triggered panel-select mode, so we know which
  // character to strip once a panel is clicked (see handleChange below).
  const pendingMentionAtIndexRef = useRef<number | null>(null)

  // Mirror value changes out through a ref so an inline callback prop can't
  // retrigger the effect.
  const onValueChangeRef = useRef(onValueChange)
  onValueChangeRef.current = onValueChange
  useEffect(() => {
    onValueChangeRef.current?.(value)
  }, [value])

  // Seed the input from an external draft (the "ask about X" entry point). Runs
  // only when `seedText` changes; the consume-callback is read from a ref so an
  // inline `onSeedConsumed` doesn't re-trigger this.
  const onSeedConsumedRef = useRef(onSeedConsumed)
  onSeedConsumedRef.current = onSeedConsumed
  useEffect(() => {
    if (!seedText) return
    setValue(seedText)
    onSeedConsumedRef.current?.()
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    })
  }, [seedText])

  // `@`-mention: typing "@" at the start of the input or right after
  // whitespace enters panel-select mode (reusing the same click-a-panel flow
  // the AI tool loop uses — see panel-selection-store.ts). `panelType: null`
  // makes every panel selectable, not just one type. Resolving replaces the
  // "@" with a mention chip; cancelling (Escape / 30s timeout / manual
  // Cancel) leaves the textarea untouched.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const el = e.target
      const newValue = el.value
      const caret = el.selectionStart ?? newValue.length
      setValue(newValue)

      // Track the pending "@" through edits: deleting it closes the panel
      // picker (Chris, 2026-09-30); typing before it shifts its index.
      const pending = pendingMentionAtIndexRef.current
      if (pending != null) {
        const delta = newValue.length - value.length
        let next: number | null = pending
        if (delta < 0 && pending >= caret) {
          // Backspace/Delete removed [caret, caret - delta) of the old value.
          next = pending < caret - delta ? null : pending + delta
        } else if (delta > 0 && caret - delta <= pending) {
          next = pending + delta
        }
        if (next == null || newValue[next] !== '@') {
          pendingMentionAtIndexRef.current = null
          usePanelSelectionStore.getState().cancelSelection()
        } else {
          pendingMentionAtIndexRef.current = next
        }
      }

      const justTypedAt =
        newValue.length === value.length + 1 &&
        caret > 0 &&
        newValue[caret - 1] === '@' &&
        (caret === 1 || /\s/.test(newValue[caret - 2]))
      if (!justTypedAt) return

      const atIndex = caret - 1
      pendingMentionAtIndexRef.current = atIndex

      usePanelSelectionStore.getState().startSelection(
        null,
        'Click a panel to mention it →',
        (panelId) => {
          useChatStore.getState().addMention({
            panelId,
            componentType: resolvePanelComponentType(panelId) ?? 'unknown',
            title: resolvePanelTitle(panelId),
          })
          const idx = pendingMentionAtIndexRef.current
          pendingMentionAtIndexRef.current = null
          if (idx != null) {
            setValue((prev) =>
              prev[idx] === '@' ? prev.slice(0, idx) + prev.slice(idx + 1) : prev,
            )
          }
          requestAnimationFrame(() => inputRef.current?.focus())
        },
        () => {
          // Cancelled or timed out — leave the textarea (including the "@") as-is.
          pendingMentionAtIndexRef.current = null
        },
        // Reference-only pick — no "making changes" activity shimmer on the panel.
        { activityOnSelect: false },
      )
    },
    [value],
  )

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue('')
  }, [value, disabled, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  // What the textarea SHOWS: the committed value plus any live overlay
  // (dictation types directly into the box as the user speaks; the final
  // transcript replaces the overlay via appendText).
  const displayValue = overlayText
    ? value && !value.endsWith(' ')
      ? `${value} ${overlayText}`
      : value + overlayText
    : value

  // Auto-resize textarea (keyed on what is DISPLAYED, so live dictation grows it)
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = '0'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [displayValue])

  const canSend = value.trim() !== '' && !disabled

  // Dictation lands here: append (with a space seam) and refocus.
  const appendText = useCallback((text: string) => {
    setValue((prev) => (prev && !prev.endsWith(' ') ? `${prev} ${text}` : prev + text))
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  // A dropped ticker lands at the caret (the end if the box was never
  // focused), with a space on either side so it never fuses with a word.
  const insertText = useCallback((text: string) => {
    const el = inputRef.current
    setValue((prev) => {
      const at =
        el && el.selectionStart != null ? Math.min(el.selectionStart, prev.length) : prev.length
      const before = prev.slice(0, at)
      const after = prev.slice(at)
      const lead = before && !/\s$/.test(before) ? ' ' : ''
      const trail = after.startsWith(' ') ? '' : ' '
      const next = before + lead + text + trail + after
      const caret = (before + lead + text + trail).length
      requestAnimationFrame(() => {
        const input = inputRef.current
        if (!input) return
        input.focus()
        input.setSelectionRange(caret, caret)
      })
      return next
    })
  }, [])

  return {
    value,
    displayValue,
    inputRef,
    handleChange,
    handleKeyDown,
    handleSubmit,
    canSend,
    appendText,
    insertText,
  }
}
